import React, {useEffect, useState} from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';

import '../admin.css';
import instance from '../../../instance';
import {CircularProgress} from '@material-ui/core';
import CallNotification from '../../../context/NotificationContext';
import {PaymentMethod} from '../../../Enums/PaymentMethod';
import HelpPopover from '../../../ui/HelpPopover';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InvoicePopup from '../../PaymentPage/Invoice/InvoicePopup';

interface IInvoiceItem {
    user_id: number;
    create_date: string;
    total_amount: number;
    payment_status?: number;
    payment_status_name: string;
    order_code: string;
    payment_method: PaymentMethod;
    invoice_number: string;
    payer_inn: string;
    payer_name: string;
}

interface IInvoiceDetail {
    user_id: number;
    create_date: string;
    total_amount: number;
    payment_status_name: string;
    order_code: string;
    payment_snapshot: string;
}

const InvoicesPage = () => {
    const [invoiceList, setInvoiceList] = useState<IInvoiceItem[]>([]);
    const [visibleList, setVisibleList] = useState<IInvoiceItem[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const [dateFrom, setDateFrom] = useState<string>('');
    const [dateTo, setDateTo] = useState<string>('');
    const [onlyPending, setOnlyPending] = useState<boolean>(true);

    const [selectedInvoice, setSelectedInvoice] = useState<IInvoiceDetail | null>(null);
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const {handleErrorNotification} = CallNotification();

    const [invoiceOpen, setInvoiceOpen] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

    const [processingOrders, setProcessingOrders] = useState<Set<string>>(new Set());

    const openInvoice = (orderId: string, userId: number) => {
        setSelectedOrderId(orderId);
        setSelectedUserId(userId);
        setInvoiceOpen(true);
    };

    useEffect(() => {
        const fetchInvoiceList = async () => {
            try {
                const response = await instance.post('/LK', {
                    method: 'GetInvoiceList',
                    params: {},
                });
                handleErrorNotification(response);

                const sortedList = (response.data?.invoice_list || []).sort(
                    (a: IInvoiceItem, b: IInvoiceItem) => new Date(b.create_date).getTime() - new Date(a.create_date).getTime(),
                );

                setInvoiceList(sortedList);

                const filteredList = sortedList.filter((item: IInvoiceItem) => item.payment_status === 0);

                setVisibleList(filteredList);
            } catch (error) {
                console.error('Ошибка при загрузке истории заказов', error);
            } finally {
                setLoading(false);
            }
        };

        void fetchInvoiceList();
    }, []);

    const handleRowClick = async (orderCode: string, userId: number) => {
        try {
            setSelectedUserId(null);

            const response = await instance.post('/LK', {
                method: 'GetPaymentInfo',
                params: {order: orderCode, user_id: userId},
            });
            handleErrorNotification(response);

            if (response.data?.payment_info) {
                setSelectedInvoice(response.data.payment_info);
                setSelectedUserId(userId);
                setDetailOpen(true);
            }
        } catch (error) {
            console.error('Ошибка при получении информации о платеже', error);
        }
    };

    const handleClose = () => {
        setDetailOpen(false);
        setSelectedInvoice(null);
    };

    const applyFilter = () => {
        let filtered = invoiceList;

        // Фильтр по статусу оплаты
        if (onlyPending) {
            filtered = filtered.filter(item => item.payment_status === 0);
        }

        // Фильтр по дате
        if (dateFrom || dateTo) {
            const start = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
            const end = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null;

            let s = start,
                e = end;
            if (s && e && s.getTime() > e.getTime()) {
                const tmp = s;
                s = e;
                e = tmp;
            }

            filtered = filtered.filter(p => {
                const d = new Date(p.create_date).getTime();
                const okStart = s ? d >= s.getTime() : true;
                const okEnd = e ? d <= e.getTime() : true;
                return okStart && okEnd;
            });
        }

        setVisibleList(filtered);
    };

    // Обновляем фильтр при изменении галочки
    useEffect(() => {
        applyFilter();
    }, [onlyPending]);

    const handleConfirmPayment = async (invoiceNumber: string, orderCode: string, e: React.MouseEvent) => {
        e.stopPropagation();

        const confirm = window.confirm(`Подтверждаете оплату счёта ${invoiceNumber}?`);
        if (!confirm) return;

        try {
            setProcessingOrders(prev => new Set(prev).add(orderCode));

            const response = await instance.post('/LK', {
                method: 'ConfirmPaymentByInvoice',
                params: {order: orderCode},
            });

            handleErrorNotification(response);

            if (response.data?.result === 'ok') {
                // Обновляем локальное состояние через 5 секунд
                setTimeout(() => {
                    setInvoiceList(prevList =>
                        prevList.map(item =>
                            item.order_code === orderCode ? {...item, payment_status: 1, payment_status_name: 'Оплачено'} : item,
                        ),
                    );
                    setVisibleList(prevList =>
                        prevList.map(item =>
                            item.order_code === orderCode ? {...item, payment_status: 1, payment_status_name: 'Оплачено'} : item,
                        ),
                    );
                    setProcessingOrders(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(orderCode);
                        return newSet;
                    });
                }, 1000);
            }
        } catch (error) {
            console.error('Ошибка при подтверждении оплаты', error);
            setProcessingOrders(prev => {
                const newSet = new Set(prev);
                newSet.delete(orderCode);
                return newSet;
            });
        }
    };

    const renderInvoiceDetails = () => {
        if (!selectedInvoice) return null;

        let snapshot;
        try {
            snapshot = JSON.parse(selectedInvoice.payment_snapshot);
        } catch {
            snapshot = null;
        }

        if (!snapshot || !Array.isArray(snapshot.items))
            return (
                <>
                    <Typography variant="subtitle1" gutterBottom>
                        Заказ № {selectedInvoice.order_code}
                    </Typography>
                    <Typography variant="body2">Дата: {new Date(selectedInvoice.create_date).toLocaleString()}</Typography>
                    <Typography variant="body2">Сумма: {selectedInvoice.total_amount} ₸</Typography>
                    <Typography variant="body2">Статус: {selectedInvoice.payment_status_name}</Typography>
                </>
            );

        const grouped = snapshot.items.reduce((acc: Record<number, any>, item: any) => {
            if (!acc[item.facial_id]) {
                acc[item.facial_id] = {
                    facial_name: item.facial_name,
                    facial_inn: item.facial_inn,
                    items: [],
                };
            }
            acc[item.facial_id].items.push(item);
            return acc;
        }, {});

        const getPriceString = (item: any) => {
            if (item.quantity && item.quantity > 1) {
                return `${item.quantity} x ${item.unit_price_no_vat} = ${item.price_no_vat}`;
            }
            return item.price_no_vat;
        };

        return (
            <>
                <Typography variant="subtitle1" gutterBottom>
                    Заказ № {selectedInvoice.order_code}
                </Typography>
                <Typography variant="body2">Дата: {new Date(selectedInvoice.create_date).toLocaleString()}</Typography>
                <Typography variant="body2">Сумма: {selectedInvoice.total_amount} ₸</Typography>
                <Typography variant="body2">Статус: {selectedInvoice.payment_status_name}</Typography>

                <Divider sx={{my: 2}} />

                {Object.values(grouped).map((group: any, idx: number) => (
                    <Box key={idx} sx={{mb: 3}}>
                        <Typography variant="subtitle2" sx={{mb: 1}}>
                            {group.facial_name} (ИНН: {group.facial_inn})
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Модель</TableCell>
                                    <TableCell>Серийный №</TableCell>
                                    <TableCell>Рег. №</TableCell>
                                    <TableCell>ОФД</TableCell>
                                    <TableCell>Без НДС</TableCell>
                                    <TableCell>С НДС</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {group.items.map((item: any, i: number) => (
                                    <TableRow key={i}>
                                        <TableCell>{item.model || '—'}</TableCell>
                                        <TableCell>{item.serial_number || '—'}</TableCell>
                                        <TableCell>{item.reg_number || '—'}</TableCell>
                                        <TableCell>{item.ofd?.name || '—'}</TableCell>
                                        <TableCell>{getPriceString(item)} ₸</TableCell>
                                        <TableCell>{item.price_with_vat} ₸</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                ))}

                <Divider sx={{my: 2}} />
                <Typography variant="body2">Итого без НДС: {snapshot.total_no_vat} ₸</Typography>
                <Typography variant="body2">
                    НДС{snapshot.vat_rate ? ` ${snapshot.vat_rate * 100}%` : ''}: {snapshot.vat} ₸
                </Typography>
                <Typography variant="body2">Итого с НДС: {snapshot.total_with_vat} ₸</Typography>
            </>
        );
    };

    return (
        <Box sx={{pt: 6, pb: 6}}>
            <Typography variant="h4" gutterBottom>
                Счета
            </Typography>

            <Paper sx={{mb: 2}}>
                <Box
                    component="form"
                    onSubmit={e => {
                        e.preventDefault();
                        applyFilter();
                    }}
                    p={2}
                    display="grid"
                    gridTemplateColumns={{xs: '1fr', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)'}}
                    gap={2}
                    alignItems="end"
                >
                    <TextField
                        label="От"
                        type="date"
                        value={dateFrom}
                        onChange={e => setDateFrom(e.target.value)}
                        InputLabelProps={{shrink: true}}
                    />
                    <TextField
                        label="До"
                        type="date"
                        value={dateTo}
                        onChange={e => setDateTo(e.target.value)}
                        InputLabelProps={{shrink: true}}
                    />
                    <Box>
                        <Button variant="outlined" onClick={applyFilter} sx={{mr: 1, height: 40}}>
                            Найти
                        </Button>
                        <Button
                            variant="text"
                            onClick={() => {
                                setDateFrom('');
                                setDateTo('');
                                setOnlyPending(true);
                                const filtered = invoiceList.filter(item => item.payment_status === 0);
                                setVisibleList(filtered);
                            }}
                            sx={{height: 40}}
                        >
                            Сброс
                        </Button>
                    </Box>

                    <Box>
                        <FormControlLabel
                            control={<Checkbox checked={onlyPending} onChange={e => setOnlyPending(e.target.checked)} color="primary" />}
                            label="Только ожидающие оплаты"
                        />
                    </Box>
                </Box>
            </Paper>

            <Paper>
                <Box p={3}>
                    {loading ? (
                        <CircularProgress />
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Дата</TableCell>
                                    <TableCell>БИН/ИИН</TableCell>
                                    <TableCell>Наименование</TableCell>
                                    <TableCell>Номер заказа</TableCell>
                                    <TableCell>
                                        Счёт
                                        <HelpPopover content={<>Номер счёта при оплате через банковские реквизиты.</>} />
                                    </TableCell>
                                    <TableCell>Сумма</TableCell>
                                    <TableCell>Статус</TableCell>
                                    <TableCell>&nbsp;</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleList.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <Typography color="text.secondary">Нет записей по выбранному фильтру</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    visibleList.map((item, index) => (
                                        <TableRow
                                            key={index}
                                            hover
                                            sx={{
                                                height: '50px',
                                                cursor: 'pointer',
                                                '& td': {
                                                    borderBottom: index === visibleList.length - 1 ? 'none' : undefined,
                                                },
                                            }}
                                            onClick={() => void handleRowClick(item.order_code, item.user_id)}
                                        >
                                            <TableCell>{new Date(item.create_date).toLocaleString()}</TableCell>
                                            <TableCell>{item.payer_inn}</TableCell>
                                            <TableCell>{item.payer_name}</TableCell>
                                            <TableCell>{item.order_code}</TableCell>
                                            <TableCell sx={{paddingTop: 0, paddingBottom: 0}}>
                                                {item?.payment_method === PaymentMethod.Invoice ? (
                                                    <>
                                                        <Tooltip title="Скачать или распечатать">
                                                            <Button
                                                                size="small"
                                                                color="primary"
                                                                variant="text"
                                                                startIcon={<DescriptionOutlinedIcon sx={{fontSize: 16}} />}
                                                                onClick={e => {
                                                                    e.stopPropagation();
                                                                    openInvoice(item.order_code, item.user_id);
                                                                }}
                                                                sx={{fontSize: 12, height: 20}}
                                                            >
                                                                {item.invoice_number}
                                                            </Button>
                                                        </Tooltip>
                                                    </>
                                                ) : (
                                                    <>&mdash;</>
                                                )}
                                            </TableCell>
                                            <TableCell>{item.total_amount} ₸</TableCell>
                                            <TableCell sx={{color: item.payment_status === 1 ? 'success.main' : 'inherit', width: '80px'}}>
                                                {item.payment_status_name}
                                            </TableCell>
                                            <TableCell sx={{minWidth: '120px'}}>
                                                {item.payment_status === 0 && (
                                                    <Tooltip title="Подтвердить оплату по счету">
                                                        <Button
                                                            size="small"
                                                            onClick={e => void handleConfirmPayment(item.invoice_number, item.order_code, e)}
                                                            disabled={processingOrders.has(item.order_code)}
                                                            sx={{
                                                                whiteSpace: 'nowrap',
                                                                minWidth: '110px',
                                                            }}
                                                        >
                                                            {processingOrders.has(item.order_code) ? 'Обработка...' : 'Оплатили'}
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    )}
                </Box>
            </Paper>

            <Dialog open={detailOpen} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>Детали платежа</DialogTitle>
                <DialogContent dividers>{selectedInvoice ? renderInvoiceDetails() : <Typography>Загрузка...</Typography>}</DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} variant="outlined">
                        Закрыть
                    </Button>
                </DialogActions>
            </Dialog>

            <InvoicePopup
                open={invoiceOpen}
                setOpen={setInvoiceOpen}
                orderId={selectedOrderId}
                userId={selectedUserId}
                onClose={() => setInvoiceOpen(false)}
            />
        </Box>
    );
};

export default InvoicesPage;
